import React, { useEffect, useState } from "react";
import { api, JOB_STATUSES, PAYMENT_STATUSES, SERVICES, CLIENT_TYPES, CUSTOMER_PAYMENT_STATUSES, PAYOUT_STATUSES, CITIES, formatError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Image as ImageIcon } from "lucide-react";

const empty = {
  customer_name: "", service_type: "Deep Cleaning",
  client_type: "Residential / Private",
  address: "", city: "", date: "", start_time: "10:00",
  estimated_hours: 2, billable_hours: 2,
  hourly_rate_excl_vat: 35, vat_rate: 9, vat_override: false, vat_notes: "",
  assigned_provider_id: "",
  quoted_price: 0, payment_status: "Not Sent", payment_paid: 0, payment_notes: "",
  owner_payout_status: "Not Ready", hallie_payout_status: "Not Ready",
  supplies_needed: "", equipment_needed: "",
  customer_notes: "", internal_notes: "", access_instructions: "",
  status: "Confirmed", completion_notes: "",
};

const OWNER_BASE_RATE_DEFAULT = 30;

function computeRevenue(form) {
  const billable = parseFloat(form.billable_hours) || 0;
  const rate = parseFloat(form.hourly_rate_excl_vat) || 0;
  const vatPct = parseFloat(form.vat_rate) || 0;
  const subtotal = +(rate * billable).toFixed(2);
  const vat = +(subtotal * vatPct / 100).toFixed(2);
  const total = +(subtotal + vat).toFixed(2);
  const owner = +(OWNER_BASE_RATE_DEFAULT * billable).toFixed(2);
  const hallie = +(subtotal - owner).toFixed(2);
  return { subtotal, vat, total, owner, hallie, vatPct, rate, billable };
}

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState({ status: "", provider_id: "", city: "", service_type: "" });
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const q = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && q.append(k, v));
    const r = await api.get(`/jobs?${q}`);
    setJobs(r.data);
  };
  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const providerName = (id) => providers.find((p) => p.id === id)?.name || "—";

  const save = async () => {
    try {
      const payload = {
        ...form,
        quoted_price: parseFloat(form.quoted_price) || 0,
        payment_paid: parseFloat(form.payment_paid) || 0,
        estimated_hours: parseFloat(form.estimated_hours) || 2,
      };
      if (openId === "new") {
        await api.post("/jobs", payload);
        toast.success("Job created");
      } else {
        await api.patch(`/jobs/${openId}`, payload);
        toast.success("Job updated");
      }
      setOpenId(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Jobs</h1>
          <p className="text-sm text-slate-500 mt-1">All scheduled and completed jobs.</p>
        </div>
        <Button onClick={() => { setOpenId("new"); setForm({ ...empty, date: new Date().toISOString().slice(0,10) }); }} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full" data-testid="job-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Job
        </Button>
      </div>

      <div className="mt-5 grid sm:grid-cols-4 gap-3">
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.provider_id} onValueChange={(v) => setFilter({ ...filter, provider_id: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All cleaners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cleaners</SelectItem>
            {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.city} onValueChange={(v) => setFilter({ ...filter, city: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All cities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.service_type} onValueChange={(v) => setFilter({ ...filter, service_type: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All services" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead><TableHead>Date</TableHead><TableHead>Service</TableHead>
              <TableHead>Cleaner</TableHead><TableHead>Status</TableHead><TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((j) => (
              <TableRow key={j.id} data-testid={`job-row-${j.id}`}>
                <TableCell>
                  <div className="font-medium">{j.customer_name}</div>
                  <div className="text-xs text-slate-500">{j.address}, {j.city}</div>
                </TableCell>
                <TableCell>{j.date} {j.start_time && <span className="text-xs text-slate-500">{j.start_time}</span>}</TableCell>
                <TableCell>{j.service_type}</TableCell>
                <TableCell>{providerName(j.assigned_provider_id)}</TableCell>
                <TableCell><StatusBadge status={j.status} /></TableCell>
                <TableCell>€{j.quoted_price || 0}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setOpenId(j.id); setForm({ ...empty, ...j }); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(j.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No jobs.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{openId === "new" ? "New job" : "Edit job"}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Customer</Label>
              <Select value={form.customer_id || ""} onValueChange={(v) => {
                const c = customers.find((x) => x.id === v);
                setForm({ ...form, customer_id: v, customer_name: c?.name || form.customer_name, address: c?.address || form.address, city: c?.city || form.city });
              }}>
                <SelectTrigger className="mt-1.5" data-testid="job-form-customer"><SelectValue placeholder="Select customer (or type name below)" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Service</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="job-form-date" /></div>
            <div><Label>Start time</Label><Input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Estimated hours</Label><Input type="number" step="0.5" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Assigned cleaner</Label>
              <Select value={form.assigned_provider_id || ""} onValueChange={(v) => setForm({ ...form, assigned_provider_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1.5" data-testid="job-form-cleaner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="job-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quoted price (€)</Label><Input type="number" value={form.quoted_price} onChange={(e) => setForm({ ...form, quoted_price: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Payment status</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount paid (€)</Label><Input type="number" value={form.payment_paid} onChange={(e) => setForm({ ...form, payment_paid: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Supplies needed</Label><Input value={form.supplies_needed} onChange={(e) => setForm({ ...form, supplies_needed: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Equipment needed</Label><Input value={form.equipment_needed} onChange={(e) => setForm({ ...form, equipment_needed: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Access instructions</Label><Textarea value={form.access_instructions} onChange={(e) => setForm({ ...form, access_instructions: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Customer notes</Label><Textarea value={form.customer_notes} onChange={(e) => setForm({ ...form, customer_notes: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Internal notes</Label><Textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Completion notes</Label><Textarea value={form.completion_notes} onChange={(e) => setForm({ ...form, completion_notes: e.target.value })} className="mt-1.5" /></div>
          </div>

          {/* Private revenue share section — admin only */}
          <RevenueShareSection form={form} setForm={setForm} />
          {(form.photos_before?.length > 0 || form.photos_after?.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              <PhotoSet label="Before" photos={form.photos_before} />
              <PhotoSet label="After" photos={form.photos_after} />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="job-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoSet({ label, photos = [] }) {
  if (!photos || !photos.length) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {label}</div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => (
          <img key={p} src={fileUrl(p)} alt={label} className="rounded-md object-cover aspect-square w-full" />
        ))}
      </div>
    </div>
  );
}

function RevenueShareSection({ form, setForm }) {
  const r = computeRevenue(form);
  const ownerBase = OWNER_BASE_RATE_DEFAULT;
  const warnings = [];
  if (r.rate && r.rate < ownerBase) warnings.push(`Hourly rate €${r.rate.toFixed(2)} is below owner base rate €${ownerBase}`);
  if (r.hallie < 0) warnings.push("Quote is below owner base rate — Hallie share is negative");
  if ((form.payment_status || "").toLowerCase() !== "paid") warnings.push("Customer payment not fully received yet");

  return (
    <div className="mt-6 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 p-5" data-testid="job-revenue-section">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Admin only · Private</span>
        <h3 className="font-heading font-semibold text-[#1B3A6B]">Revenue share</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Never shown to the customer, cleaner, or on invoices. Owner base rate is fixed at €{ownerBase}/h excl. VAT.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label>Client type</Label>
          <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v, vat_rate: v === "Commercial / B2B" ? 21 : 9 })}>
            <SelectTrigger className="mt-1.5" data-testid="job-client-type"><SelectValue /></SelectTrigger>
            <SelectContent>{CLIENT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Billable hours</Label>
          <Input type="number" step="0.5" value={form.billable_hours} onChange={(e) => setForm({ ...form, billable_hours: e.target.value })} className="mt-1.5" data-testid="job-billable-hours" />
        </div>
        <div>
          <Label>Hourly rate excl. VAT (€)</Label>
          <Input type="number" step="0.5" value={form.hourly_rate_excl_vat} onChange={(e) => setForm({ ...form, hourly_rate_excl_vat: e.target.value })} className="mt-1.5" data-testid="job-hourly-rate" />
        </div>
        <div>
          <Label>VAT rate %</Label>
          <Input type="number" step="0.5" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value, vat_override: true })} className="mt-1.5" data-testid="job-vat-rate" />
        </div>
        <div>
          <Label>Customer payment status</Label>
          <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{CUSTOMER_PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Owner payout status</Label>
          <Select value={form.owner_payout_status} onValueChange={(v) => setForm({ ...form, owner_payout_status: v })}>
            <SelectTrigger className="mt-1.5" data-testid="job-owner-payout-status"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYOUT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Hallie payout status</Label>
          <Select value={form.hallie_payout_status} onValueChange={(v) => setForm({ ...form, hallie_payout_status: v })}>
            <SelectTrigger className="mt-1.5" data-testid="job-hallie-payout-status"><SelectValue /></SelectTrigger>
            <SelectContent>{PAYOUT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <Label>VAT notes (optional)</Label>
          <Input value={form.vat_notes} onChange={(e) => setForm({ ...form, vat_notes: e.target.value })} placeholder="e.g. reverse-charge, override reason…" className="mt-1.5" />
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
        <div className="bg-white rounded-lg p-3 ring-1 ring-slate-200">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Customer (excl. VAT)</div>
          <div className="font-heading text-lg font-bold text-[#1B3A6B] mt-0.5">€{r.subtotal.toFixed(2)}</div>
          <div className="text-[11px] text-slate-500">+ VAT €{r.vat.toFixed(2)} = <b>€{r.total.toFixed(2)}</b></div>
        </div>
        <div className="bg-white rounded-lg p-3 ring-1 ring-emerald-200">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">Owner share</div>
          <div className="font-heading text-lg font-bold text-emerald-700 mt-0.5">€{r.owner.toFixed(2)}</div>
          <div className="text-[11px] text-slate-500">€{ownerBase}/h × {r.billable.toFixed(1)}h</div>
        </div>
        <div className={`bg-white rounded-lg p-3 ring-1 ${r.hallie < 0 ? "ring-rose-300" : "ring-slate-200"}`}>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Hallie share</div>
          <div className={`font-heading text-lg font-bold mt-0.5 ${r.hallie < 0 ? "text-rose-700" : "text-[#1B3A6B]"}`}>€{r.hallie.toFixed(2)}</div>
          <div className="text-[11px] text-slate-500">(€{r.rate.toFixed(2)} − €{ownerBase}) × {r.billable.toFixed(1)}h</div>
        </div>
      </div>

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-amber-900 bg-amber-100/60 rounded-md p-2.5">
          {warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}
    </div>
  );
}
